import React from 'react';
import { Plus, X } from 'lucide-react';
import { MultiplierPoolEntry } from '../../../../core/types/cascade-slot.types';

interface MultiplierPoolEditorProps {
  pool: MultiplierPoolEntry[];
  onChange: (pool: MultiplierPoolEntry[]) => void;
}

export const MultiplierPoolEditor: React.FC<MultiplierPoolEditorProps> = ({
  pool,
  onChange,
}) => {
  const totalWeight = pool.reduce((sum, e) => sum + e.weight, 0);

  const updateEntry = (index: number, updates: Partial<MultiplierPoolEntry>) => {
    const updated = pool.map((e, i) => (i === index ? { ...e, ...updates } : e));
    onChange(updated);
  };

  const addEntry = () => {
    const lastValue = pool.length > 0 ? pool[pool.length - 1].value : 1;
    onChange([...pool, { value: lastValue * 2, weight: 1 }]);
  };

  const removeEntry = (index: number) => {
    onChange(pool.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-1 text-xs text-zinc-500 px-1">
        <span>Value</span>
        <span>Weight</span>
        <span>Chance</span>
        <span></span>
      </div>
      {pool.map((entry, index) => {
        const chance = totalWeight > 0 ? ((entry.weight / totalWeight) * 100).toFixed(3) : '0';
        return (
          <div key={index} className="grid grid-cols-[1fr_1fr_auto_auto] gap-1 items-center px-1">
            <input
              className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-xs text-amber-400 font-semibold w-full text-center"
              type="number"
              value={entry.value}
              min={1}
              onChange={(e) => updateEntry(index, { value: parseInt(e.target.value) || 1 })}
            />
            <input
              className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-xs text-zinc-300 w-full text-center"
              type="number"
              value={entry.weight}
              min={0}
              onChange={(e) => updateEntry(index, { weight: parseInt(e.target.value) || 0 })}
            />
            <span className="text-[10px] text-zinc-500 w-14 text-right">{chance}%</span>
            <button
              className="p-0.5 rounded hover:bg-zinc-700 text-zinc-500 hover:text-red-400"
              onClick={() => removeEntry(index)}
              title="Remove"
            >
              <X size={10} />
            </button>
          </div>
        );
      })}
      <div className="flex justify-between text-xs text-zinc-500 px-1 pt-1 border-t border-zinc-700">
        <span>Total Weight</span>
        <span>{totalWeight.toLocaleString()}</span>
      </div>
      <button
        className="flex items-center gap-1 px-1 py-0.5 text-xs text-zinc-400 hover:text-zinc-200"
        onClick={addEntry}
      >
        <Plus size={12} />
        Add Entry
      </button>
    </div>
  );
};
